import { LandingPageContent } from '../types';

/**
 * Age long-tail page completing the 3/4/5 ladder under the gifts-for-kids
 * pillar. Five overlaps with school start in most markets, which gives this
 * page its distinct angle (early reading, school transitions).
 */
const giftsForFiveYearOlds: LandingPageContent = {
	path: '/gifts/gifts-for-5-year-olds',
	seoTitle: 'Gifts for 5-Year-Olds — Ideas for New Schoolkids and Early Readers',
	metaDescription:
		'Gift ideas for 5-year-olds: personalized storybooks for beginning readers, coloring books and keepsakes. What suits the first year of school, and what to skip.',
	heading: 'Gifts for 5-Year-Olds',
	subheading: 'The year the letters start meaning something.',
	heroImage: '/assets/hero/kid-3/flat_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Five is a threshold year. Somewhere in it, most children start school, and somewhere in it the alphabet stops being decoration and starts being *code*. A five-year-old is often a person who can nearly read — which is the most giftable condition a child ever passes through.

Because here is the honest problem with learning to read: the books are boring. Early readers are, of necessity, simple — and a child who is working that hard deserves better material than "the cat sat".

The fix is to make the child the material. A simple story is not boring when it is about you.`,
	sections: [
		{
			heading: 'What works at five',
			body: `**Personalized storybooks for a nearly-reader.** The single best fit. The child recognises their own name instantly — usually the first word any child learns to read — and that name appears throughout the book, giving them islands of confidence on every page. The rest arrives read-aloud until, one day, it doesn't.

**Personalized coloring books.** Still very much alive at five, with visibly better results than a year ago — expect actual colour schemes.

**School-adjacent morale.** Starting school is a big transition wearing a small uniform. A story in which the child is brave, capable and the hero of the day does quiet, real work in September.

**Keepsakes with their character on.** A t-shirt or mug of their illustrated self starts having social value now — five-year-olds show things to their friends.`,
		},
		{
			heading: 'What to skip at five',
			body: `- **Baby-coded gifts.** Five-year-olds are freshly, fiercely *not babies* and will audit gifts for any suggestion otherwise.
- **Full chapter books.** Close, but not yet — picture-led stories still win.
- **Ninety-piece anything.** Ambitions exceed patience at this age; a puzzle should be finishable inside one sitting or it becomes a floor hazard.
- **The thing every classmate has.** School has begun, and with it the great homogenisation of toys. Personalization is the reliable way out — no classmate has a book about *them*.`,
		},
		{
			heading: 'The name is the hook',
			body: `Reading researchers have a term for it — the "own-name advantage". A child's name is the first written word they fixate on, the first they can spot at speed, the first they write.

A personalized book leans on that advantage deliberately. Every appearance of the name is a page the child *can* partly read, which changes their posture toward the whole book: they stop being audience and start being reader. For a five-year-old wobbling on the edge of literacy, that shift is the entire game — more on this in [why personalized books help kids read](/blog/why-personalized-books-help-kids-read).`,
		},
		{
			heading: 'Buying for a fifth birthday vs. school start',
			body: `They deserve different gifts. A **birthday** at five is a public event with an audience of peers — the reveal moment of a personalized book plays brilliantly, and the book survives the party.

**School start** is quieter and more nervous. A story about being brave on a big day, starring the child, given the week before term — that is not really a present; it is equipment. Some families do both, with different stories, and report no complaints from the recipient.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'Their name on every page — reading practice they volunteer for.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Still a favourite at five, with visibly prouder results.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'Their illustrated self — now with playground prestige.',
		},
	],
	faqs: [
		{
			question: 'What is a good gift for a 5-year-old?',
			answer:
				'Five-year-olds are usually beginning readers, so personalized storybooks — where the child’s own name and face carry them through the story — fit the age unusually well. Coloring books and custom keepsakes with their illustrated character also land reliably.',
		},
		{
			question: 'What is a good gift for a child starting school?',
			answer:
				'A personalized story in which the child is brave and capable on a big day does real work for school nerves. Given the week before term starts, it functions less as a present and more as equipment.',
		},
		{
			question: 'Can a 5-year-old read a personalized book by themselves?',
			answer:
				'Usually not yet — and that is fine. The child’s name is typically the first word they read confidently, and its repetition through the book gives them footholds while an adult reads the rest. The handover to solo reading happens gradually, often with this exact kind of book.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Gifts for 4-year-olds', href: '/gifts/gifts-for-4-year-olds' },
		{ label: 'Birthday gifts for kids', href: '/gifts/birthday-gifts-for-kids' },
		{
			label: 'Why personalized books help kids read',
			href: '/blog/why-personalized-books-help-kids-read',
		},
	],
	cta: { label: 'Browse gifts', href: '/products' },
};

export default giftsForFiveYearOlds;
